import queryString from 'query-string';
import auth from '../module/auth';
import Config from '../module/config';
import { CacheOptionCategories, ListItemNames } from '../utils/enum';
import { buildKeyForSessionStorage } from '../utils/helper';
import userManager from '../utils/userManager';
import ListItem_ from './models/ListItem';
import ResponseBase_ from './models/ResponseBase';

export const PROMPT = {
  LOGIN: 'login',
  NONE: 'none',
};

type AuthPars = {
  InstanceId: string,
  prompt?: string,
  InvitationEmail?: string
  AutoLogout?: string
}

export type CacheOption = {
  category: CacheOptionCategories,
  categoryName: (typeof ListItemNames)[keyof typeof ListItemNames] | string,
}

export default class BaseAPI {
  static getBasicHeaders() {
    return {
      'Content-Type': Config.Api.Headers.ContentType,
      'x-ms-version': Config.Api.Headers.ApiVersion,
    };
  }

  static getRequestHeaders() {
    return {
      Authorization: Config.Api.Headers.AuthBase + auth.getToken(),
      'Content-Type': Config.Api.Headers.ContentType,
      Accept: Config.Api.Headers.Accept,
      'x-ms-version': Config.Api.Headers.ApiVersion,
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: '0',
      'X-XSRF-TOKEN': document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN'))?.split('=')[1],
    };
  }

  static getTokenHeaders() {
    return {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: Config.Api.Headers.Accept,
    };
  }

  static getTokenHeadersReset() {
    return {
      Accept: Config.Api.Headers.Accept,
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-ms-version': Config.Api.Headers.ApiVersion,
    };
  }

  static encodeURLparameters(details) {
    const formBody = [];
    Object.keys(details).forEach(property => {
      const encodedKey = encodeURIComponent(property);
      const encodedValue = encodeURIComponent(details[property]);
      formBody.push(`${encodedKey}=${encodedValue}`);
    });

    return formBody.join('&');
  }

  static perform(promise) {
    return promise.then(({ Result: res, Exception: exp, }) => {
      if (exp) {
        BaseAPI.onError(exp);
      }

      return res;
    }, BaseAPI.onError);
  }

  static performPlat = promise => promise.then(_ => _, BaseAPI.onError);

  static onError = exp => {
    const exception = exp.response || exp;

    const qstrings = queryString.parse((window as any).location.search);

    switch (exception.status) {
      case 401:
        BaseAPI.signinRedirect(qstrings);
        break;
      case 403:
        BaseAPI.signinRedirect(qstrings);
        break;
      case 500:
        console.error(exp.responseText);
        // (window as any).errorManager.captureException(exp.responseText, true);
        break;
      default:
        // TODO
        break;
    }
  };

  static signinRedirect(qstrings) {
    // eslint-disable-next-line no-underscore-dangle
    const store = userManager.settings.userStore._store;

    store.setItem('redirectLocalBackTo', window.location.pathname.replace(window.APP_NAME, ''));

    const pars = BaseAPI.getPars(qstrings);

    userManager.signinRedirect({ extraQueryParams: pars, });
  }

  static promiseWrapper = <T>(axiosPromise, cacheOption?: CacheOption): Promise<ResponseBase_<T>> => {
    return new Promise(resolve => {
      userManager.getUser().then(user => {
        if (user) {
          const { profile, } = user;

          if ((profile || {}).InstitutionId !== window.institutionId) {
            BaseAPI.signinRedirect({ prompt: PROMPT.LOGIN, });
          }
        }

        axiosPromise.then(response => {
          const data = JSON.parse(response.data);

          if (cacheOption?.category === CacheOptionCategories.ListItems && data.errorCode <= 0) {
            const key = buildKeyForSessionStorage(cacheOption);
            const listItems = data.result;
            window.sessionStorage.setItem(key, JSON.stringify(listItems));
          }

          return resolve(data);
        }).catch(BaseAPI.onError);
      });
    });
  };

  static getPars = ({
    prompt, InvitationEmail, email, autologout,
  }) => {
    const res: AuthPars = {
      InstanceId: window.institutionId,
    };

    // We comment out this code in case LV-15292 and LV-15637
    // If we want to use silent login we don't have to set the 'login' value on the prompt field
    // res.prompt = prompt || PROMPT.LOGIN;

    if (prompt) {
      res.prompt = prompt;
    }

    if (InvitationEmail) res.InvitationEmail = InvitationEmail;

    if (email) res.InvitationEmail = email;

    if (autologout) res.AutoLogout = autologout;

    return res;
  }
}
